import { Column } from "primereact/column";
import { useTranslation } from "react-i18next";
import i18n from "../../i18n";
import { useRef, useState } from "react";
import { RenderedHelper } from "../../utils/render/RenderedHelper";
import { FormattedUtils } from "../../utils/FormattedUtils";
import InputSwitch from "../../component/input/InputSwitch";
import { Button } from "primereact/button";
import { ReactComponent as BriefcaseIcon } from "../../assets/images/briefcase.svg";
import MatchBagDialog from "./dialogs/MatchBagDialog";
import ConfirmDialog from "../../component/confirmdialog/ConfirmDialog";
import { ReactComponent as WarningTriangleIcon } from "../../assets/images/warning-triangle-icon.svg";
import { useMemo , useEffect, useContext} from "react";
import { MessageContext } from "../../context/MessageContext";
import { DeviceService } from "../../service/DeviceService";
import { RouteService } from "../../service/RouteService";
import { CashLevelService } from "../../service/CashLevelService";
import DataTable, {
    FILTER_TYPES,
    DataTableContext,
} from "../../component/datatable2";import CashReconciliationSidebar from "./CashReconciliationSidebar";
import ActionButton from "../../component/actionbutton/ActionButton";
import ActionMenu from "../../component/actionmenu/ActionMenu";
import ReconciliationSuccessDialog from "./dialogs/ReconciliationSuccessDialog";
import { ReactComponent as CheckBagIcon } from "../../assets/images/cash-operations/check-bag.svg";
import { ReactComponent as CancelCheckBagIcon } from "../../assets/images/cash-operations/cancel-check-bag.svg";


const CashReconciliation = () => {
    const { t } = useTranslation();
    const tableRef = useRef(null);
    const menuRef = useRef(null);
    const [showOnlyNotReconciled, setShowOnlyNotReconciled] = useState(false);
    const [matchBagDialogVisible, setMatchBagDialogVisible] = useState(false);
    const [bagNotFoundDialogVisible, setBagNotFoundDialogVisible] = useState(false);
    const [searchedBagId, setSearchedBagId] = useState('');
    const [successDialogVisible, setSuccessDialogVisible] = useState(false);
    const [discrepancyDialogVisible, setDiscrepancyDialogVisible] = useState(false);
    const [deviceOptions, setDeviceOptions] = useState([]);
    const [specialistOptions, setSpecialistOptions] = useState([]);
    const isMounted = useRef(false);
    const { showErrorMessage, showSuccessMessage } = useContext(MessageContext);
    const [discrepancyPending, setDiscrepancyPending] = useState(null);
    const [discrepancySaving, setDiscrepancySaving] = useState(false);
    const [successPending, setSuccessPending] = useState(null);
    const [successSaving, setSuccessSaving] = useState(false);
    const [statusOptions, setStatusOptions] = useState([]);

    const [cancelDialogVisible, setCancelDialogVisible] = useState(false);
    const [cancelPendingRow, setCancelPendingRow] = useState(null);
    const [cancelSaving, setCancelSaving] = useState(false);

    const { searchCriteria: reconciliationSearchCriteria, setSearchCriteria: setReconciliationSearchCriteria } = useContext(DataTableContext);
    const [reconciliationSidebarState, setReconciliationSidebarState] = useState({ 
        visible: false, 
        viewMode: 'NEW', 
        reconciliationData: null 
    });

    const handleOpenNewReconciliation = () => {
        setMatchBagDialogVisible(true);
    };

    const canUseRowActions = (row) => row.status === 'APPOINTMENT';

    const handleCheckBag = (row) => {
        if (!canUseRowActions(row)) return;

        setReconciliationSidebarState({
            visible: true,
            viewMode: 'EXISTING',
            reconciliationData: row,
        });
    };


    const generateMenuItems = (row) => {
        const disabled = row.status !== 'APPOINTMENT';

        return [
            {
                label: t('cashoperations.checkBag', 'Check bag'),
                icon: <CheckBagIcon className="action-menu-svg" />,
                key: `check-bag-${row.operationId}`,
                disabled,
                className: disabled ? 'action-menu-item--disabled' : undefined,
                command: () => handleCheckBag(row),
            },
            {
                label: t('cashoperations.cancelCheckBag', 'Cancel check bag'),
                icon: <CancelCheckBagIcon className="action-menu-svg" />,
                key: `cancel-check-bag-${row.operationId}`,
                separatorBefore: true,
                variant: 'danger',
                disabled,
                className: disabled ? 'action-menu-item--disabled' : undefined,
                command: () => {
                    setCancelPendingRow(row);
                    setCancelDialogVisible(true);
                },
            }
        ];
    };

    const actionsBody = (row) => (
        <ActionButton
            menuRef={menuRef}
            generateMenuItems={() => generateMenuItems(row)}
            showMainNavigation={false}
            mainNavigateTo={null}
        >
            {null}
        </ActionButton>
    );

    const handleMatchBagSearch = async (bagId) => {
        if (!bagId || bagId.trim() === '') {
            showErrorMessage(t('cashoperations.error.bagIdRequired') || 'Bag ID is required');
            return;
        }

        const trimmedBagId = bagId.trim();
        setSearchedBagId(trimmedBagId);

        try {
            const reconciliation = await CashLevelService.findReconciliationByBagId(trimmedBagId);
            setMatchBagDialogVisible(false);
            if (reconciliation) {
                setReconciliationSidebarState({
                    visible: true,
                    viewMode: 'EXISTING',
                    reconciliationData: reconciliation
                });
            } else {
                setBagNotFoundDialogVisible(true);
            }
        } catch (err) {
            setMatchBagDialogVisible(false);
            if (err?.status === 404) {
                setBagNotFoundDialogVisible(true);
            } else {
                console.error('Error finding reconciliation by bag ID:', err);
                showErrorMessage(t('cashoperations.error.findReconciliation'));
            }
        }
    };

    const handleCreateNewReconciliation = () => {
        setBagNotFoundDialogVisible(false);
        setReconciliationSidebarState({ 
            visible: true, 
            viewMode: 'NEW', 
            reconciliationData: { bagId: searchedBagId } 
        });
    };

    const tableToolbarRight = (
        <>
            <InputSwitch
                label={t('cashoperations.showOnlyNotReconciled')}
                checked={showOnlyNotReconciled}
                onChange={(e) => {
                    setShowOnlyNotReconciled(!!e.value);
                    tableRef.current?.refresh();
                }}
            />
            <Button
                className="small"
                onClick={handleOpenNewReconciliation}
            >
                <BriefcaseIcon />
                <span className="p-button-label pl-2">{t('cashoperations.matchBag')}</span>
            </Button>
        </>
    );

    const fetchDeviceOptions = async () => {
        try {
            const devices = await DeviceService.getDevicesForCashOperations();
            if (isMounted.current) {
                setDeviceOptions(devices.map(device => ({ id: device.id, label: device.name })));
            }
        } catch (err) {
            showErrorMessage(t('reports.sales.error.fetchDeviceOptions'));
        }
    }

    const fetchStatusOptions = async () => {
        try {
            const statuses = await CashLevelService.getReconciliationStatusOptions();
            if (isMounted.current) {
                setStatusOptions(statuses.map(status => ({ id: status, label: t(`cashoperations.reconciliationStatus.${status}`) })));
            }
        } catch (err) {
            showErrorMessage(t('reports.sales.error.fetchStatusOptions'));
        }
    }

    const fetchSpecialistOptions = async () => {
        try {
            const technicians = await RouteService.getTechniciansList();
            const techniciansData = Array.isArray(technicians) ? technicians : (technicians?.data || []);
            if (isMounted.current) {
                setSpecialistOptions(techniciansData.map(t => ({ id: t.id, label: t.name })));
            }
        } catch (err) {
            showErrorMessage(t('cashoperations.error.fetchSpecialistOptions'));
        }
    }
    useEffect(() => {
        isMounted.current = true;
        fetchDeviceOptions();
        fetchSpecialistOptions();
        fetchStatusOptions();
        return () => {
            isMounted.current = false;
        };
    }, []);

    const enums = useMemo(() => [
        {
            name: 'CashReconciliationStatus',
            values: statusOptions.map(r => ({ key: r.id, value: r.label }))
        }
    ], [statusOptions]);

    const filterConfig = useMemo(() => [
        {
            fieldName: 'operationId',
            label: t('cashoperations.operationId'),
            type: FILTER_TYPES.TEXT,
            apiParamName: 'operation_id',
        },
        {
            fieldName: 'dateTime',
            label: t('cashoperations.dateTime'),
            type: FILTER_TYPES.DATE_TIME_RANGE,
            apiParamName: 'date_time',
        },
        {
            fieldName: 'deviceName',
            label: t('cashoperations.device'),
            type: FILTER_TYPES.LIST,
            params: deviceOptions,
            apiParamName: 'device_ids',
            apiValueConverter: value => value.map(v => v.id).join(',')
        },
        {
            fieldName: 'specialistName',
            label: t('cashoperations.technician'),
            type: FILTER_TYPES.LIST,
            params: specialistOptions,
            apiParamName: 'specialist_ids',
            apiValueConverter: value => value.map(v => v.id).join(',')
        },
        {
            fieldName: 'routeId',
            label: t('cashoperations.routeId'),
            apiParamName: 'route_id',
            type: FILTER_TYPES.TEXT,
        },
        {
            fieldName: 'bagId',
            label: t('cashoperations.bagId'),
            type: FILTER_TYPES.TEXT,
            apiParamName: 'bag_id',
        },
        {
            fieldName: 'actualAmount',
            label: t('cashoperations.actualAmount'),
            type: FILTER_TYPES.AMOUNT,
            apiParamName: 'actual_amount',
        },
        {
            fieldName: 'enteredAmount',
            label: t('cashoperations.enteredAmount'),
            type: FILTER_TYPES.AMOUNT,
            apiParamName: 'entered_amount',
        },
        {
            fieldName: 'differenceAmount',
            label: t('cashoperations.differenceAmount'),
            type: FILTER_TYPES.AMOUNT,
            apiParamName: 'difference_amount',
            },
        {
            fieldName: 'status',
            label: t('cashoperations.status'),
            type: FILTER_TYPES.LIST,
            params: statusOptions,
            apiParamName: 'status',
            apiValueConverter: value => value.map(r => r.id).join(',')
        }
    ], [t, deviceOptions, specialistOptions, statusOptions]);

    const defaultSearchCriteria = useMemo(() => ({
        first: 0,
        rows: 25,
        sortField: 'dateTime',
        sortOrder: -1,
        baseQuery: '',
        filters: {},
    }), []);

    const normalizeReconciliationCriteria = (criteria) => ({
        ...defaultSearchCriteria,
        ...criteria,
        first: criteria?.first ?? defaultSearchCriteria.first,
        rows: criteria?.rows ?? defaultSearchCriteria.rows,
        sortField:
            !criteria?.sortField || criteria.sortField === 'null'
                ? defaultSearchCriteria.sortField
                : criteria.sortField,
        sortOrder:
            criteria?.sortOrder === null || criteria?.sortOrder === undefined || criteria?.sortOrder === 0
                ? defaultSearchCriteria.sortOrder
                : criteria.sortOrder,
        baseQuery: criteria?.baseQuery ?? defaultSearchCriteria.baseQuery,
        filters: criteria?.filters ?? defaultSearchCriteria.filters,
    });

    const fetchData = async (criteria) => {
        try {
            return await CashLevelService.getReconciliations(
                normalizeReconciliationCriteria(criteria),
                showOnlyNotReconciled,
                enums
            );
        } catch (err) {
            console.error('CashLevelService::getReconciliations err', err);
            showErrorMessage(t('cashoperations.error.fetch'));
            return { data: [], totalRecords: 0 };
        }
    };

    return (
        <div className='cash-operations-container'>
            <ActionMenu ref={menuRef} />
            <DataTable
                ref={tableRef}
                fetchData={fetchData}
                filterConfig={filterConfig}
                tableToolbarRight={tableToolbarRight}
                defaultSearchCriteria={defaultSearchCriteria}
            >
                <Column field="dateTime" header={t('cashoperations.dateTime')} sortable sortField="dateTime" id="dateTime"
                    headerClassName="dt2-header-wrap"
                    body={row => RenderedHelper.formatTimestamp(row.dateTime)}
                    style={{ minWidth: '130px', maxWidth: '150px' }}
                />
                <Column field="deviceName" header={t('cashoperations.device')} sortable sortField="deviceName" id="deviceName"
                    style={{ minWidth: '140px', maxWidth: '180px' }}
                />
                <Column field="specialistName" header={t('cashoperations.specialist')} sortable sortField="specialistName" id="specialistName"
                    headerClassName="dt2-header-wrap"
                    style={{ minWidth: '130px', maxWidth: '160px' }}
                />
                <Column field="routeId" header={t('cashoperations.routeId')} sortable sortField="routeId" id="routeId"
                    headerClassName="dt2-header-wrap"
                    style={{ minWidth: '100px', maxWidth: '120px' }}
                />
                <Column field="bagId" header={t('cashoperations.bagId')} sortable sortField="bagId" id="bagId"
                    headerClassName="dt2-header-wrap"
                    style={{ minWidth: '100px', maxWidth: '120px' }}
                />
                <Column field="actualAmount" header={t('cashoperations.actualAmount')} sortable sortField="actualAmount" id="actualAmount"
                    headerClassName="dt2-header-wrap"
                    body={row => row.actualAmount != null ? FormattedUtils.formatPrice(row.actualAmount) : ''}
                    style={{ minWidth: '120px', maxWidth: '140px' }}
                />
                <Column field="enteredAmount" header={t('cashoperations.enteredAmount')} sortable sortField="enteredAmount" id="enteredAmount"
                    headerClassName="dt2-header-wrap"
                    body={row => row.enteredAmount != null ? FormattedUtils.formatPrice(row.enteredAmount) : ''}
                    style={{ minWidth: '120px', maxWidth: '140px' }}
                />
                <Column field="differenceAmount" header={t('cashoperations.differenceAmount')} sortable sortField="differenceAmount" id="differenceAmount"
                    headerClassName="dt2-header-wrap"
                    style={{ minWidth: '130px', maxWidth: '150px' }}
                    body={row => {
                        const amount = row.differenceAmount;
                        if (amount === null || amount === undefined) return '';

                        const color = amount === 0 ? 'var(--accent-green)' : 'var(--accent-red)';
                        const formattedAmount = FormattedUtils.formatPrice(amount);

                        return <span style={{ color }}>{formattedAmount}</span>;
                    }}
                />
                <Column field="status" header={t('cashoperations.status')} sortable sortField="status" id="status"
                    body={row => RenderedHelper.formatEnum(row.status, 'ReconciliationStatus')}
                    style={{ minWidth: '120px', maxWidth: '140px' }}
                />
                <Column
                    key="actions"
                    field="actions"
                    headerClassName="dt2-actions-column"
                    bodyClassName="dt2-actions-column"
                    className="dt2-actions-column"
                    body={actionsBody}
                    style={{ width: '80px', minWidth: '80px', maxWidth: '80px' }}
                />
            </DataTable>
         
            <MatchBagDialog
                visible={matchBagDialogVisible}
                onHide={() => setMatchBagDialogVisible(false)}
                onSearch={handleMatchBagSearch}
            />
            <ConfirmDialog
                visible={bagNotFoundDialogVisible}
                onHide={() => setBagNotFoundDialogVisible(false)}
                onCancel={() => setBagNotFoundDialogVisible(false)}
                onConfirm={handleCreateNewReconciliation}
                delayedConfirm={false}
                title={t('cashoperations.bagNotFound')}
                messageHeader={() => (
                    <div className="flex align-items-center gap-2">
                        <WarningTriangleIcon style={{ color: '#F97316', fontSize: '20px' }} />
                        <span style={{ color: '#F97316' }}>{t('cashoperations.bagNotFoundWarning')}</span>
                    </div>
                )}
                message={
                    <div>
                        {(() => {
                            const bagNumber = searchedBagId;
                            const rawMessage = i18n.getResource(i18n.language, 'translation', 'cashoperations.bagNotFoundMessage');
                            const rawQuestion = i18n.getResource(i18n.language, 'translation', 'cashoperations.bagNotFoundQuestion');

                            if (!rawMessage) {
                                return (
                                    <>
                                        {t('cashoperations.bagNotFoundMessage', { bagNumber })}
                                        <br />
                                        {rawQuestion || t('cashoperations.bagNotFoundQuestion', { bagNumber })}
                                    </>
                                );
                            }

                            const messageParts = rawMessage.split('{bagNumber}');
                            if (messageParts.length === 2) {
                                return (
                                    <>
                                        {messageParts[0]}
                                        <strong>{bagNumber}</strong>
                                        {messageParts[1]}
                                        <br />
                                        {rawQuestion}
                                    </>
                                );
                            }
                            return (
                                <>
                                    {t('cashoperations.bagNotFoundMessage', { bagNumber })}
                                    <br />
                                    {rawQuestion || t('cashoperations.bagNotFoundQuestion', { bagNumber })}
                                </>
                            );
                        })()}
                    </div>
                }
                actionButtonLabel={t('cashoperations.createRecord')}
                cancelButtonLabel={t('cashoperations.cancel')}
                variant="none"
            />
            <ConfirmDialog
                visible={successDialogVisible}
                onHide={() => {
                    setSuccessDialogVisible(false);
                    setSuccessPending(null);
                }}
                onCancel={() => {
                    setSuccessDialogVisible(false);
                    setSuccessPending(null);
                }}
                onConfirm={async () => {
                    if (!successPending || successSaving) return;
                    setSuccessSaving(true);
                    try {
                        if (successPending.mode === 'NEW') {
                            await CashLevelService.createReconciliation(successPending.createRequest);
                            showSuccessMessage(t('cashoperations.reconciliationSidebar.success.created'));
                        } else {
                            await CashLevelService.updateReconciliation(successPending.operationId, {
                                actualAmount: successPending.actualAmount
                            });
                            showSuccessMessage(t('cashoperations.reconciliationSidebar.success.updated'));
                        }
                        setSuccessDialogVisible(false);
                        setSuccessPending(null);
                        setReconciliationSidebarState({ visible: false, viewMode: 'NEW', reconciliationData: null });
                        tableRef.current?.refresh();
                    } catch (err) {
                        console.error('Success dialog save error:', err);
                        showErrorMessage(
                            successPending.mode === 'NEW'
                                ? (t('cashoperations.reconciliationSidebar.error.create'))
                                : (t('cashoperations.reconciliationSidebar.error.update'))
                        );
                    } finally {
                        setSuccessSaving(false);
                    }
                }}
                delayedConfirm={false}
                title={t('cashoperations.amountRegisteredSuccessfully')}
                message={
                    <div>
                        {(() => {
                            const bagNumber = successPending?.bagId || '';
                            const message = t('cashoperations.amountRegisteredMessage');
                            const rawQuestion = i18n.getResource(i18n.language, 'translation', 'cashoperations.confirmReconciliationQuestion');
                            const questionParts = rawQuestion ? rawQuestion.split('{bagNumber}') : [];
                            const question = questionParts.length === 2
                                ? <>{questionParts[0]}<strong>{bagNumber}</strong>{questionParts[1]}</>
                                : t('cashoperations.confirmReconciliationQuestion', { bagNumber });
                            return (
                                <>
                                    {message}
                                    <br />
                                    {question}
                                </>
                            );
                        })()}
                    </div>
                }
                actionButtonLabel={t('cashoperations.ok')}
                cancelButtonLabel={t('cashoperations.cancel')}
                variant="none"
            />
            <ConfirmDialog
                visible={discrepancyDialogVisible}
                onHide={() => {
                    setDiscrepancyDialogVisible(false);
                    setDiscrepancyPending(null);
                }}
                onCancel={() => {
                    setDiscrepancyDialogVisible(false);
                    setDiscrepancyPending(null);
                }}
                onConfirm={async () => {
                    if (!discrepancyPending || discrepancySaving) return;
                    setDiscrepancySaving(true);
                    try {
                        if (discrepancyPending.mode === 'NEW') {
                            await CashLevelService.createReconciliation(discrepancyPending.createRequest);
                            showSuccessMessage(t('cashoperations.reconciliationSidebar.success.created'));
                        } else {
                            await CashLevelService.updateReconciliation(discrepancyPending.operationId, {
                                actualAmount: discrepancyPending.actualAmount
                            });
                            showSuccessMessage(t('cashoperations.reconciliationSidebar.success.updated'));
                        }
                        setDiscrepancyDialogVisible(false);
                        setDiscrepancyPending(null);
                        setReconciliationSidebarState({ visible: false, viewMode: 'NEW', reconciliationData: null });
                        tableRef.current?.refresh();
                    } catch (err) {
                        console.error('Discrepancy save error:', err);
                        showErrorMessage(
                            discrepancyPending.mode === 'NEW'
                                ? (t('cashoperations.reconciliationSidebar.error.create'))
                                : (t('cashoperations.reconciliationSidebar.error.update'))
                        );
                    } finally {
                        setDiscrepancySaving(false);
                    }
                }}
                delayedConfirm={false}
                title={t('cashoperations.discrepancyTitle')}
                messageHeader={() => (
                    <div className="flex align-items-center gap-2">
                        <WarningTriangleIcon style={{ color: '#F97316', fontSize: '20px' }} />
                        <span style={{ color: '#F97316' }}>{t('cashoperations.discrepancyWarning')}</span>
                    </div>
                )}
                message={
                    <div>
                        {(() => {
                            const entered = discrepancyPending?.enteredAmount != null ? Number(discrepancyPending.enteredAmount) : 0;
                            const actual = discrepancyPending?.actualAmount != null ? Number(discrepancyPending.actualAmount) : 0;
                            const discrepancyAmount = actual - entered;
                            const currency = discrepancyPending?.currency;
                            const formattedDiscrepancy = FormattedUtils.formatPrice(Math.abs(discrepancyAmount), currency);
                            const detectedMessage = t('cashoperations.discrepancyDetected');
                            const question = t('cashoperations.discrepancyQuestion');
                            return (
                                <>
                                    {detectedMessage}:{' '}
                                    <span style={{ color: 'var(--accent-red)' }}>
                                        {discrepancyAmount < 0 ? '-' : '+'}{formattedDiscrepancy}
                                    </span>
                                    <br />
                                    {question}
                                </>
                            );
                        })()}
                    </div>
                }
                actionButtonLabel={t('cashoperations.ok')}
                cancelButtonLabel={t('cashoperations.correctAmount')}
                variant="none"
            />
            <CashReconciliationSidebar
                visible={reconciliationSidebarState.visible}
                onHide={() => setReconciliationSidebarState({ visible: false, viewMode: 'NEW', reconciliationData: null })}
                onSuccess={() => {
                    setReconciliationSidebarState({ visible: false, viewMode: 'NEW', reconciliationData: null });
                    tableRef.current?.refresh();
                }}
                viewMode={reconciliationSidebarState.viewMode}
                reconciliationData={reconciliationSidebarState.reconciliationData}
                onDiscrepancyMismatch={(payload) => {
                    setDiscrepancyPending(payload);
                    setDiscrepancyDialogVisible(true);
                }}
                onMatchConfirm={(payload) => {
                    setSuccessPending(payload);
                    setSuccessDialogVisible(true);
                }}
            />

            <ReconciliationSuccessDialog
                visible={successDialogVisible}
                bagId={successPending?.bagId}
                onHide={() => {
                    setSuccessDialogVisible(false);
                    setSuccessPending(null);
                }}
                onConfirm={async () => {
                    if (!successPending || successSaving) return;

                    setSuccessSaving(true);

                    try {
                        if (successPending.mode === 'NEW') {
                            await CashLevelService.createReconciliation(successPending.createRequest);
                            showSuccessMessage(t('cashoperations.reconciliationSidebar.success.created'));
                        } else {
                            await CashLevelService.updateReconciliation(successPending.operationId, {
                                actualAmount: successPending.actualAmount,
                            });
                            showSuccessMessage(t('cashoperations.reconciliationSidebar.success.updated'));
                        }

                        setSuccessDialogVisible(false);
                        setSuccessPending(null);
                        setReconciliationSidebarState({
                            visible: false,
                            viewMode: 'NEW',
                            reconciliationData: null,
                        });
                        tableRef.current?.refresh();
                    } catch (err) {
                        console.error('Success dialog save error:', err);
                        showErrorMessage(
                            successPending.mode === 'NEW'
                                ? t('cashoperations.reconciliationSidebar.error.create')
                                : t('cashoperations.reconciliationSidebar.error.update')
                        );
                    } finally {
                        setSuccessSaving(false);
                    }
                }}
            />

            <ConfirmDialog
                visible={cancelDialogVisible}
                onHide={() => {
                    setCancelDialogVisible(false);
                    setCancelPendingRow(null);
                }}
                onCancel={() => {
                    setCancelDialogVisible(false);
                    setCancelPendingRow(null);
                }}
                onConfirm={async () => {
                    if (!cancelPendingRow || cancelSaving) return;

                    setCancelSaving(true);

                    try {
                        await CashLevelService.cancelReconciliation(cancelPendingRow.operationId);
                        showSuccessMessage(t('cashoperations.success.cancelCheckBag'));
                        setCancelDialogVisible(false);
                        setCancelPendingRow(null);
                        tableRef.current?.refresh();
                    } catch (err) {
                        console.error('Cancel check bag error:', err);
                        showErrorMessage(t('cashoperations.error.cancelCheckBag'));
                    } finally {
                        setCancelSaving(false);
                    }
                }}
                delayedConfirm={false}
                title={t('cashoperations.cancelBagReconciliation', 'Cancel bag reconciliation')}
                message={t(
                    'cashoperations.cancelBagReconciliationMessage',
                    'The selected bag will be marked as unreconciled and its status will change to Canceled. This action applies only to the selected record, and the bag will no longer be available for standard reconciliation from this list.'
                )}
                actionButtonLabel={t('cashoperations.confirmCancellation', 'Confirm cancellation')}
                cancelButtonLabel={t('cashoperations.cancel', 'Cancel')}
                variant="danger"
            />
        </div>
    )
}

export default CashReconciliation;